import { flattenObject, sortObjectProp } from './flat-object';

/**
 * The addresses step's two "same as …" answers. They are local-only UI state: neither key exists on
 * `EmpTabsFormModelV2024` / `EmpTabsFormModelV2026`, so `stripMask.strip().parse()` (zod) drops both
 * from every model the step emits.
 */
export interface AddressSameAsAnswers {
  kontaktniAdresaQuestion?: boolean;
  prechodnaAdresaQuestion?: boolean;
}

/** The `personalPart` slice the answers are derived from. */
export interface AddressSameAsSource {
  permanentniAdresa?: unknown;
  kontaktniAdresa?: unknown;
  prechodnaAdresa?: unknown;
}

/** Order-insensitive comparable form of an address, so key order never affects equality. */
const normalize = (address: unknown): string =>
  JSON.stringify(
    sortObjectProp(flattenObject((address as Record<string, unknown>) || {}))
  );

/**
 * Resolves the two "same as …" answers for a model that has just been pushed into the addresses
 * step, preferring the employee's own `remembered` answers over address equality.
 *
 * Equality may only ever *seed* an answer that doesn't exist yet. It must not recompute one the
 * employee has already given: because the flags are stripped from every emitted model (see
 * {@link AddressSameAsAnswers}), they come back missing on any push originating outside the step,
 * and re-deriving would then silently flip an explicit "No" back to "Yes" while the two addresses
 * still happen to match — i.e. in the window after the employee has opted to enter a separate
 * contact address but before they have typed it. The step's `submit()` treats that flag as
 * authoritative and copies `permanentniAdresa` over `kontaktniAdresa`, so the reverted answer used
 * to discard the address the employee went on to enter.
 *
 * An answer is omitted (rather than set to `false`) while its source address is still empty, so the
 * caller can `Object.assign` the result without clobbering an existing value.
 */
export const resolveAddressSameAsAnswers = (
  addresses: AddressSameAsSource,
  remembered: AddressSameAsAnswers = {}
): AddressSameAsAnswers => {
  const permanentniAdresa = normalize(addresses?.permanentniAdresa);
  const kontaktniAdresa = normalize(addresses?.kontaktniAdresa);
  const prechodnaAdresa = normalize(addresses?.prechodnaAdresa);

  const answers: AddressSameAsAnswers = {};
  if (permanentniAdresa !== '{}') {
    answers.kontaktniAdresaQuestion =
      remembered?.kontaktniAdresaQuestion ??
      permanentniAdresa === kontaktniAdresa;
  }
  if (kontaktniAdresa !== '{}') {
    answers.prechodnaAdresaQuestion =
      remembered?.prechodnaAdresaQuestion ??
      kontaktniAdresa === prechodnaAdresa;
  }
  return answers;
};
